<div class="p-2">
  <!-- Header Section -->
  <div class="flex flex-wrap justify-between gap-2 items-center mb-6">
    <div>
      <h1 class="text-2xl font-bold text-gray-800" i18n>
        {{ isFsrFilter ? 'My Customers' : 'Customer Management' }}
      </h1>
      <p class="text-gray-600" i18n>
        {{ isFsrFilter ? 'Customers you have relationships with through reservations, contracts, or direct additions.' : 'Manage customers and their business information.' }}
      </p>
      <!-- FSR Filter Status -->
      <div *ngIf="isFsrFilter" class="mt-2 flex items-center gap-2">
        <i class="pi pi-user text-blue-500"></i>
        <span class="text-sm font-medium text-blue-700 bg-blue-50 px-3 py-1 rounded-full" i18n>
          Showing customers for {{ fsrName }}
        </span>
        <!-- Only show clear button if current user is not an FSR (i.e., admin viewing specific FSR's customers) -->
        <p-button *ngIf="!isFSR()" appGlobalLoadingDisable 
          icon="pi pi-times" 
          [text]="true" 
          size="small" 
          severity="secondary" 
          (onClick)="clearFsrFilter()"
          [attr.aria-label]="'Clear FSR filter and view all customers'"
          [pTooltip]="'View all customers'"
          i18n-aria-label
          i18n-pTooltip>
        </p-button>
      </div>
    </div>
    <div class="flex gap-3">
      <ng-container *hasPermission="'customer.create'">
        <p-button 
          *ngIf="isFSR()"
          label="Customer Lookup"
          icon="pi pi-plus"
          (onClick)="addNewCustomer()" 
          [disabled]="loading"
          i18n-label
          [attr.aria-label]="'Add new customer to the system'"
          i18n-aria-label>
        </p-button>
      </ng-container>
    </div>
  </div>

  <!-- Search and Filters -->
  <p-card styleClass="mb-6">
    <div class="flex flex-col lg:flex-row gap-4">
      <!-- Search Input -->
      <div class="flex-1">
        <label for="customer-search" class="sr-only" i18n>Search customers</label>
        <p-iconfield iconPosition="left">
          <p-inputicon>
            <i class="pi pi-search" aria-hidden="true"></i>
          </p-inputicon>
          <input 
            pInputText 
            type="text" 
            id="customer-search"
            placeholder="Search customers..." i18n-placeholder
            [(ngModel)]="searchText" 
            (keyup.enter)="applyFilters()" 
            class="min-w-[200px]" 
            fluid
            i18n-placeholder
            [attr.aria-label]="'Search customers by business name, contact details, or other information'"
            i18n-aria-label>
        </p-iconfield>
      </div>

      <!-- Filters -->
      <div class="flex flex-wrap gap-3">
        <p-select 
          [options]="industryOptions" 
          [(ngModel)]="selectedIndustry" 
          placeholder="Industry: All" i18n-placeholder
          optionLabel="label" 
          optionValue="value" 
          styleClass="min-w-[150px]"
          [loading]="loadingIndustries"
          [disabled]="loadingIndustries"
          i18n-placeholder
          [attr.aria-label]="'Filter customers by industry'"
          i18n-aria-label>
          <ng-template pTemplate="loading">
            <app-loading-spinner size="small" type="inline" i18n-message message="Loading industries..."></app-loading-spinner>
          </ng-template>
        </p-select>

        <p-select 
          [options]="interestLevelOptions" 
          [(ngModel)]="selectedInterestLevel" 
          placeholder="Interest: All" i18n-placeholder
          optionLabel="label" 
          optionValue="value" 
          styleClass="min-w-[150px]"
          i18n-placeholder
          [attr.aria-label]="'Filter customers by interest level'"
          i18n-aria-label>
        </p-select>

        <!-- <p-select [options]="promotionalGiftsOptions" [(ngModel)]="selectedPromotionalGifts" placeholder="Gifts: All"
          optionLabel="label" optionValue="value" styleClass="min-w-[150px]">
        </p-select> -->
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-2">
        <p-button 
          label="Search" 
          icon="pi pi-search" 
          (onClick)="applyFilters()" 
          [disabled]="loading"
          [attr.aria-busy]="loading"
          i18n-label
          [attr.aria-label]="'Search and filter customers'"
          i18n-aria-label>
        </p-button>
        <p-button 
          label="Reset" 
          icon="pi pi-refresh" 
          severity="secondary" 
          [outlined]="true" 
          (onClick)="resetFilters()"
          [disabled]="loading"
          i18n-label
          [attr.aria-label]="'Reset all filters to default values'"
          i18n-aria-label>
        </p-button>
      </div>
    </div>
  </p-card>

  <!-- Customers Table -->
  <p-card class="!p-3">
    <div class="overflow-x-auto min-h-screen">
      <p-table [value]="customers" [loading]="loading" [paginator]="false" [rows]="pageSize"
        [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries" i18n-currentPageReportTemplate
        [tableStyle]="{'min-width': '1600px', 'width': '100%'}" styleClass="p-datatable-sm">

        <!-- Header -->
        <ng-template pTemplate="header">
          <tr>
            <th class="min-w-[250px]" i18n>BUSINESS NAME</th>
            <th *ngIf="isSA()" class="min-w-[150px]" i18n>ADDED BY FSR</th>
            <th class="min-w-[120px]" i18n>ZIP CODE</th>
            <th class="min-w-[140px]" i18n>EMPLOYEES</th>
            <th class="min-w-[120px]" i18n>INTEREST LEVEL</th>
            <th class="min-w-[140px]" i18n>ACTIVE RESERVATIONS</th>
            <th class="min-w-[140px]" i18n>EXPIRED RESERVATIONS</th>
            <th class="min-w-[140px]" i18n>ACTIVE CONTRACTS</th>
            <th class="min-w-[140px]" i18n>EXPIRED CONTRACTS</th>
            <th 
              class="min-w-[120px] cursor-pointer hover:bg-gray-50 transition-colors duration-200"
              (click)="toggleSort()"
              (keydown.enter)="toggleSort()"
              (keydown.space)="toggleSort(); $event.preventDefault()"
              tabindex="0"
              role="button"
              [attr.aria-label]="'Sort by created date, currently ' + (sortOrder === -1 ? 'descending' : 'ascending')"
              i18n-aria-label>
              <div class="flex items-center justify-between">
                <span i18n>CREATED</span>
                <i class="pi" [ngClass]="sortOrder === -1 ? 'pi-sort-down' : 'pi-sort-up'" aria-hidden="true"></i>
              </div>
            </th>
            <th class="min-w-[180px]" i18n>ACTIONS</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-customer>
          <tr 
            class="cursor-pointer group hover:bg-gray-50 transition-colors duration-200"
            (click)="viewCustomer(customer._id)"
            (keydown.enter)="viewCustomer(customer._id)"
            (keydown.space)="viewCustomer(customer._id); $event.preventDefault()"
            tabindex="0"
            role="button"
            [attr.aria-label]="'View customer ' + customer.businessName"
            i18n-aria-label>
            <!-- Business Name -->
            <td class="min-w-[200px] group-hover:bg-gray-50 transition-colors duration-200">
              <div class="flex items-center gap-3">
                <div class="">
                  <p-avatar [label]="customer.businessName.charAt(0).toUpperCase()" size="normal"
                  [style]="{'background-color': '#0061DD', 'color': '#FFFFFF'}" shape="circle">
                </p-avatar>
                </div>
               
                <div>
                  <!-- <div class="font-semibold">{{ customer.businessName.split(' ').length > 2 ?
                    customer.businessName.split(' ').slice(0, 2).join(' ') + '...' : customer.businessName }}</div> -->
                    <div class="font-semibold">{{ customer.businessName }}</div>
                  <div class="text-sm text-gray-500">{{ customer.industry }}</div>
                  @if (customer.refNo) {
                  <div class="text-xs text-gray-400 mt-1">{{ customer.refNo }}</div>
                  }
                </div>
              </div>
            </td>

            <!-- Added By FSR (only for SA users) -->
            <td *ngIf="isSA()" class="min-w-[150px] group-hover:bg-gray-50 transition-colors duration-200">
              <span class="text-sm text-gray-700">{{ customer.addedByFsr || 'N/A' }}</span>
            </td>

            <!-- Zip Code -->
            <td class="min-w-[120px] group-hover:bg-gray-50 transition-colors duration-200">
              <span class="text-sm text-gray-500">{{ customer.zipCode }}</span>
            </td>

            <!-- Number of Employees -->
            <td class="min-w-[140px] group-hover:bg-gray-50 transition-colors duration-200">
              <span class="text-sm text-gray-500">{{ customer.numberOfEmployees }}</span>
            </td>

            <!-- Interest Level -->
            <td class="min-w-[120px] group-hover:bg-gray-50 transition-colors duration-200">
              <p-tag [value]="getInterestLevelLabel(customer.interestLevel)"
                [severity]="getInterestLevelBadgeSeverity(customer.interestLevel)">
              </p-tag>
            </td>

            <!-- Active Reservations -->
            <td class="min-w-[140px] group-hover:bg-gray-50 transition-colors duration-200">
              <span class="text-sm text-gray-900 font-medium">{{ customer.activeReservationsCount ?? 0 }}</span>
            </td>

            <!-- Expired Reservations -->
            <td class="min-w-[140px] group-hover:bg-gray-50 transition-colors duration-200">
              <span class="text-sm text-gray-900 font-medium">{{ customer.expiredReservationsCount ?? 0 }}</span>
            </td>

            <!-- Active Contracts -->
            <td class="min-w-[140px] group-hover:bg-gray-50 transition-colors duration-200">
              <span class="text-sm text-gray-900 font-medium">{{ customer.activeContractsCount ?? 0 }}</span>
            </td>

            <!-- Expired Contracts -->
            <td class="min-w-[140px] group-hover:bg-gray-50 transition-colors duration-200">
              <span class="text-sm text-gray-900 font-medium">{{ customer.expiredContractsCount ?? 0 }}</span>
            </td>

            <!-- Created Date -->
            <td class="min-w-[120px] group-hover:bg-gray-50 transition-colors duration-200">
              <span class="text-sm text-gray-500">{{ formatDate(customer.createdAt) }}</span>
            </td>

            <!-- Actions -->
            <td class="min-w-[180px] group-hover:bg-gray-50 transition-colors duration-200">
              <div class="flex items-center gap-2" (click)="$event.stopPropagation()">
                <div>
                  <p-button appGlobalLoadingDisable 
                    label="View Customer" 
                    size="small" 
                    [outlined]="true"
                    (onClick)="viewCustomer(customer._id)"
                    i18n-label
                    [attr.aria-label]="'View details for ' + customer.businessName"
                    i18n-aria-label>
                  </p-button>
                </div>

                <div class="relative">
                  <p-button appGlobalLoadingDisable 
                    icon="pi pi-ellipsis-v" 
                    size="small"
                    severity="secondary"
                    [text]="true"
                    (onClick)="showActionMenu($event, customer)"
                    [attr.aria-label]="'More actions for ' + customer.businessName"
                    [attr.aria-haspopup]="true"
                    [attr.aria-expanded]="false"
                    [pTooltip]="'Actions'"
                    i18n-aria-label
                    i18n-pTooltip>
                  </p-button>
                </div>
              </div>
            </td>
          </tr>
        </ng-template>

        <!-- Empty State -->
        <ng-template pTemplate="emptymessage">
          <tr>
            <td [attr.colspan]="isSA() ? 11 : 10" class="text-center py-8">
              <div class="flex flex-col items-center gap-2" role="status">
                <i class="pi pi-building text-4xl text-gray-300" aria-hidden="true"></i>
                <p class="text-gray-500" i18n>No customers found</p>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>

      <!-- Pagination -->
      <nav class="flex flex-wrap justify-between gap-2 mt-4" [attr.aria-label]="'Customer list pagination'" i18n-aria-label>
        <div class="text-sm text-gray-600" role="status" aria-live="polite">
          <span i18n>Showing</span> {{ (currentPage - 1) * pageSize + 1 }}<span i18n> to </span>{{ Math.min(currentPage * pageSize, totalCustomers) }}<span i18n> of </span>{{ totalCustomers }}<span i18n> entries</span>
        </div>

        <p-paginator 
          [rows]="pageSize" 
          [totalRecords]="totalCustomers" 
          [rowsPerPageOptions]="pageSizes"
          (onPageChange)="onPageChange($event)" 
          [showCurrentPageReport]="false"
          [attr.aria-label]="'Navigate through customer pages'"
          i18n-aria-label>
        </p-paginator>
      </nav>
    </div>
  </p-card>

  <!-- Action Menu (outside table for better positioning) -->
  <p-menu 
    #menu 
    [popup]="true" 
    [model]="actionMenuItems" 
    appendTo="body"
    role="menu"
    [attr.aria-label]="'Customer actions menu'"
    i18n-aria-label>
  </p-menu>

  <!-- Delete Loading Overlay -->
  <div *ngIf="deletingCustomer" class="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
    <app-loading-spinner size="large" type="full-screen" i18n-message message="Deleting customer..."></app-loading-spinner>
  </div>

  <!-- Customer Review Drawer -->
  <p-drawer 
    [(visible)]="isDrawerVisible" 
    position="right" 
    [modal]="true"
    styleClass="!w-full md:!w-[30rem] lg:!w-[60rem]"
    role="dialog"
    [attr.aria-label]="'Customer details panel'"
    [attr.aria-modal]="true"
    i18n-aria-label>

    <ng-template pTemplate="headless">
      <div class="flex flex-col h-full">
        <!-- Header -->
        <header class="flex items-center justify-between px-6 py-2 shrink-0 border-b border-gray-200">
          <h2 class="text-lg font-semibold" id="customer-details-heading" i18n>Customer Details</h2>
          <p-button appGlobalLoadingDisable 
            icon="pi pi-times" 
            [text]="true" 
            [rounded]="true" 
            (onClick)="closeCustomerReview()"
            [attr.aria-label]="'Close customer details panel'"
            i18n-aria-label>
          </p-button>
        </header>

        <!-- Content -->
        <main class="flex-1 overflow-y-auto p-6" aria-labelledby="customer-details-heading">
          <!-- Loading State -->
          @if (customerReviewLoading) {
          <div class="flex flex-col items-center justify-center py-8" role="status" aria-live="polite">
            <i class="pi pi-spin pi-spinner text-4xl text-blue-500 mb-4" aria-hidden="true"></i>
            <p class="text-gray-600" i18n>Loading customer details...</p>
          </div>
          }

          <!-- Customer Details -->
          @if (!customerReviewLoading && selectedCustomerForReview) {
          <div class="space-y-6">
            <!-- Business Information -->
            <div class="space-y-4">
              <h3 class="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2" i18n>Business Information</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1" i18n>Business Name</label>
                  <p class="text-gray-900">{{ selectedCustomerForReview.businessName }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1" i18n>Business ID</label>
                  <p class="text-gray-900 truncate md:max-w-[20ch] lg:max-w-[120ch]">{{ selectedCustomerForReview.businessId }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1" i18n>Industry</label>
                  <p class="text-gray-900">{{ selectedCustomerForReview.industry }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1" i18n>Zip Code</label>
                  <p class="text-gray-900">{{ selectedCustomerForReview.zipCode }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1" i18n>Number of Employees</label>
                  <p class="text-gray-900">{{ selectedCustomerForReview.numberOfEmployees }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1" i18n>Interest Level</label>
                  <p-tag [value]="getInterestLevelLabel(selectedCustomerForReview.interestLevel)"
                    [severity]="getInterestLevelBadgeSeverity(selectedCustomerForReview.interestLevel)" class="mt-1">
                  </p-tag>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1" i18n>Promotional Gifts</label>
                  <p-tag [value]="selectedCustomerForReview.acceptedPromotionalGifts ? 'Accepted' : 'Not Accepted'"
                    [severity]="selectedCustomerForReview.acceptedPromotionalGifts ? 'success' : 'danger'" class="mt-1">
                  </p-tag>
                </div>
              </div>
            </div>

            <!-- Decision Makers -->
            <div class="space-y-4">
              <h3 class="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2" i18n>Decision Makers</h3>
              @if (selectedCustomerForReview.decisionMaker && selectedCustomerForReview.decisionMaker.length > 0) {
                @for (decisionMaker of selectedCustomerForReview.decisionMaker; track decisionMaker.email) {
                  <div class="p-4 bg-gray-50 rounded-lg">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1" i18n>Name</label>
                        <p class="text-gray-900">{{ decisionMaker.salutation }} {{ decisionMaker.firstName }} {{ decisionMaker.lastName }}</p>
                      </div>

                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1" i18n>Email</label>
                        <p class="text-gray-900">
                          <a href="mailto:{{ decisionMaker.email }}"
                            class="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200">{{
                            decisionMaker.email }}</a>
                        </p>
                      </div>

                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1" i18n>Mobile Number</label>
                        <p class="text-gray-900">
                          <a href="tel:{{ decisionMaker.mobileNumber }}"
                            class="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200">{{
                            decisionMaker.mobileNumber }}</a>
                        </p>
                      </div>
                    </div>
                  </div>
                }
              } @else {
                <p class="text-gray-500 italic" i18n>No decision makers specified</p>
              }
            </div>

            <!-- Documents -->
            <div class="space-y-4">
              <h3 class="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2" i18n>Attachments</h3>
              @if (customerDocuments.length > 0) {
                <div class="space-y-2">
                  @for (doc of customerDocuments; track doc; let i = $index) {
                    <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div class="flex items-center gap-3 flex-1 min-w-0">
                        <i [class]="getFileIcon(doc).icon + ' ' + getFileIcon(doc).color + ' text-xl'"></i>
                        <span class="text-sm text-gray-700 truncate" [title]="getDocumentDisplayName(doc, i)">
                          {{ getDocumentDisplayName(doc, i) }}
                        </span>
                      </div>
                      <div class="flex items-center gap-2 shrink-0">
                        <p-button 
                          appGlobalLoadingDisable 
                          icon="pi pi-external-link" 
                          [text]="true" 
                          [rounded]="true"
                          (onClick)="openDocument(doc)"
                          [pTooltip]="'Open in New Tab'"
                          [attr.aria-label]="'Open ' + getDocumentDisplayName(doc, i) + ' in new tab'"
                          i18n-pTooltip>
                        </p-button>
                      </div>
                    </div>
                  }
                </div>
              } @else {
                <p class="text-gray-500 italic" i18n>No attachments uploaded</p>
              }
            </div>

            <!-- Additional Information -->
            <div class="space-y-4">
              <h3 class="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2" i18n>Additional Information</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1" i18n>Created Date</label>
                  <p class="text-gray-900">{{ selectedCustomerForReview.createdAt ?
                    formatDate(selectedCustomerForReview.createdAt) : 'N/A' }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1" i18n>Last Updated</label>
                  <p class="text-gray-900">{{ selectedCustomerForReview.updatedAt ?
                    formatDate(selectedCustomerForReview.updatedAt) : 'N/A' }}</p>
                </div>
              </div>
            </div>
          </div>
          }
        </main>
      </div>
    </ng-template>
  </p-drawer>

  <!-- Confirmation Dialogs -->
  <p-confirmDialog></p-confirmDialog>

  <!-- Toast Messages -->
  <p-toast></p-toast>
</div>
